/* ═══════════════════════════════════════════════════════════════════════════
 * IMPORT DU POINTAGE MENSUEL — L'APERÇU D'ABORD, L'ÉCRITURE ENSUITE (lot C)
 * ═══════════════════════════════════════════════════════════════════════════
 * (06/10/2026.)
 *
 * ⛔ CET ÉCRAN NE CONNAÎT AUCUN CLASSEUR. Les intitulés de colonnes, le nom de
 *    la feuille et les codes de statut sont SAISIS par l'utilisateur, et aucune
 *    valeur n'est pré-remplie — pas même en exemple. *Un exemple de code de
 *    statut se recopie, et le premier client qui n'emploie pas celui-là importe
 *    un fichier entier en « statut inconnu ».* (Même raison qui interdit un
 *    montant en placeholder sur l'écran de révision de grille.)
 *
 * ⚠️ DEUX BOUTONS, DEUX ACTES, ET L'ÉCRITURE EST VERROUILLÉE TANT QUE L'APERÇU
 *    N'A PAS EU LIEU. *Un seul bouton « Importer » qui afficherait le résultat
 *    après coup ferait de l'aperçu un compte rendu.*
 *
 * ⚠️ LE REMPLACEMENT SE DEMANDE, PUIS SE CONFIRME. Il n'est proposé que quand
 *    l'aperçu a trouvé des jours déjà pointés : *proposer d'écraser avant de
 *    savoir s'il y a quelque chose à écraser invite au geste.*
 */
import { useState } from 'react';
import api from 'services/api';
import useEscapeClose from 'hooks/useEscapeClose';

/* Les statuts du PRODUIT — les cibles du mapping. L'utilisateur déclare, pour
 * chacun, les valeurs de cellule de SON classeur.
 * ⓘ Ordre P/R/A d'abord : les trois de la convention, les plus saisis. */
/* ⛔ LE LIBELLÉ DIT LE STATUT, ET RIEN DE PLUS (consigne de Dom, 06/10/2026).
 *    Ces libellés affirmaient un effet de paie — pour l'absence, pour le congé,
 *    pour la maladie et pour le retard — **écrit en dur dans un écran**.
 *    C'était exact sur la déduction et muet
 *    sur le reste : aucun régime ne s'appliquait, et un congé était payé comme
 *    un jour travaillé. *Une demi-vérité qui rassure coûte plus cher qu'un vide
 *    qui se voit.*
 * ➡️ L'EFFET VIENT DE `GET /pointages/effets` — donc du référentiel du PAYS et
 *    de la surcharge du tenant — ou s'affiche « à renseigner ». La recette du
 *    lot relie cet écran à cette route : un libellé qui réaffirmerait un effet
 *    fait ÉCHOUER le test.
 * ⓘ `abandon` est une cible RÉSERVÉE : la déclarer permet au client de nommer
 *   son code, et chaque ligne qui la porte est ensuite refusée avec sa phrase
 *   propre — l'abandon de poste en cours de vacation est une anomalie de
 *   contrôle terrain, pas un statut de pointage. */
const CIBLES = [
  { cle: 'present', libelle: 'Présent' },
  { cle: 'repos', libelle: 'Repos' },
  { cle: 'retard', libelle: 'Retard' },
  { cle: 'absent', libelle: 'Absent' },
  { cle: 'conge', libelle: 'Congé' },
  { cle: 'maladie', libelle: 'Maladie' },
  { cle: 'demission', libelle: 'Démission' },
  { cle: 'abandon', libelle: 'Abandon de poste (sera refusé, et le refus dit où le déclarer)' },
];

/* ⓘ CET ÉCRAN N'AFFICHE AUCUN EFFET DE PAIE, ET C'EST DÉLIBÉRÉ. Il fait
 *   correspondre les codes d'un classeur à des statuts : la question posée ici
 *   est « que veut dire cette case ? », pas « que paie-t-elle ? ». L'effet lu au
 *   référentiel s'affiche sur l'écran de pointage, à côté de chaque statut —
 *   un seul endroit l'annonce. *Une table de libellés d'effet recopiée ici
 *   serait une seconde réponse à la même question, et rien ne garantirait
 *   qu'elles restent d'accord.* */

const LIBELLE_MOTIF = {
  matricule_inconnu: 'Matricule inconnu de cette société',
  hors_contrat: 'Agent pas encore embauché, ou déjà sorti, à cette date',
  date_hors_mois: 'Date hors du mois déclaré',
  date_illisible: 'Date illisible',
  statut_inconnu: 'Valeur de statut non déclarée au mapping',
  doublon_fichier: 'Deux fois le même agent le même jour dans le fichier',
  deja_pointe: 'Un pointage existe déjà pour cet agent ce jour-là',
  abandon_hors_pointage: 'Abandon de poste : à déclarer comme anomalie de contrôle terrain',
};

const MOIS_FR = ['', 'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet',
  'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'];

export default function ImportPointageModale({ mois, annee, onFerme, onEcrit }) {
  const [fichier, setFichier] = useState(null);
  const [forme, setForme] = useState('grille');
  const [feuille, setFeuille] = useState('');
  const [ligneEntetes, setLigneEntetes] = useState(1);
  const [colMatricule, setColMatricule] = useState('');
  const [colDate, setColDate] = useState('');
  const [colStatut, setColStatut] = useState('');
  /* Pour la grille : le motif des intitulés de colonnes de jour. `#` est
   * remplacé par le numéro du jour. */
  const [motifJour, setMotifJour] = useState('');
  const [equivalents, setEquivalents] = useState({});
  const [remplacer, setRemplacer] = useState(false);

  const [apercu, setApercu] = useState(null);
  const [erreur, setErreur] = useState('');
  const [enCours, setEnCours] = useState('');

  useEscapeClose(true, onFerme);

  const joursDuMois = new Date(Date.UTC(annee, mois, 0)).getUTCDate();

  /* ⚠️ TOUT CHANGEMENT DE MAPPING OU DE FICHIER INVALIDE L'APERÇU. *Sans cela,
   *    l'utilisateur pourrait prévisualiser un fichier et confirmer l'écriture
   *    d'un autre — le serveur le refuserait, mais l'écran l'aurait invité.* */
  const invalider = () => { setApercu(null); setErreur(''); };

  const majEquivalent = (cle, texte) => {
    invalider();
    setEquivalents((e) => ({ ...e, [cle]: texte }));
  };

  const mapping = () => {
    const eq = {};
    for (const c of CIBLES) {
      const brut = (equivalents[c.cle] || '').split(',').map((x) => x.trim()).filter(Boolean);
      if (brut.length) eq[c.cle] = brut;
    }
    const m = {
      forme,
      feuille: feuille.trim() || null,
      ligne_entetes: Number(ligneEntetes) || 1,
      mois, annee,
      colonne_matricule: colMatricule.trim(),
      equivalents: eq,
    };
    if (forme === 'colonnes') {
      m.colonne_date = colDate.trim();
      m.colonne_statut = colStatut.trim();
    } else {
      /* ⓘ Les colonnes de jour sont ENGENDRÉES depuis le motif saisi et le
       *   nombre de jours du mois affiché : un mois de 30 jours ne déclare pas
       *   un 31. *Laisser saisir 31 intitulés à la main serait une invitation à
       *   la faute, et la faute serait silencieuse.* */
      m.colonnes_jours = Array.from({ length: joursDuMois }, (_, k) => ({
        jour: k + 1,
        intitule: (motifJour || '').replace(/#/g, String(k + 1)),
      }));
    }
    return m;
  };

  const corps = (avecConfirmation) => {
    const fd = new FormData();
    fd.append('file', fichier);
    fd.append('mapping', JSON.stringify(mapping()));
    fd.append('remplacer', remplacer ? 'true' : 'false');
    if (avecConfirmation) fd.append('confirme', 'true');
    return fd;
  };

  const direErreur = (e) => {
    const d = e.response?.data;
    if (!d) { setErreur('La demande n a pas abouti.'); return; }
    /* ⚠️ ON AFFICHE LE REFUS DU SERVEUR, PAS UNE PHRASE À NOUS. Le serveur nomme
     *    le mois, le nombre de pièces, les colonnes lues — le réécrire ici
     *    perdrait ce qu'il a mesuré. */
    setErreur(d.error || 'La demande n a pas abouti.');
    if (d.refus_par_motif) setApercu({ ...d, ecrit: false });
  };

  const demanderApercu = async () => {
    if (!fichier) { setErreur('Choisir un fichier.'); return; }
    setEnCours('apercu'); setErreur(''); setApercu(null);
    try {
      const { data } = await api.post('/pointages/import/apercu', corps(false));
      setApercu(data);
    } catch (e) { direErreur(e); } finally { setEnCours(''); }
  };

  const ecrire = async () => {
    setEnCours('ecriture'); setErreur('');
    try {
      const { data } = await api.post('/pointages/import', corps(true));
      setApercu(data);
      if (onEcrit) onEcrit(data);
    } catch (e) { direErreur(e); } finally { setEnCours(''); }
  };

  const t = apercu?.totaux;
  const dejaPointes = (apercu?.refus_par_motif || [])
    .find((m) => m.motif === 'deja_pointe')?.effectif || 0;

  return (
    <div className="gss-modal-overlay" onClick={onFerme}>
      <div className="gss-modal" style={{ maxWidth: 1000, width: '96vw' }} onClick={(e) => e.stopPropagation()}>
        <div className="gss-modal-header">
          <h2>Importer le pointage — {MOIS_FR[mois]} {annee}</h2>
          <button className="gss-modal-close" onClick={onFerme}>&times;</button>
        </div>

        <div style={{ padding: 16, maxHeight: '70vh', overflowY: 'auto' }}>
          {/* ── LE FICHIER ─────────────────────────────────────────────── */}
          <div className="gss-field">
            <label>Classeur (.xlsx, .xls ou .csv)</label>
            <input type="file" accept=".xlsx,.xls,.csv" className="gss-input"
              onChange={(e) => { invalider(); setFichier(e.target.files?.[0] || null); }} />
          </div>

          {/* ── LA FORME ───────────────────────────────────────────────── */}
          <div className="gss-field">
            <label>Forme du classeur</label>
            <select className="gss-input" value={forme}
              onChange={(e) => { invalider(); setForme(e.target.value); }}>
              <option value="grille">Grille mensuelle — une ligne par agent, une colonne par jour</option>
              <option value="colonnes">En colonnes — une ligne par agent et par jour</option>
            </select>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div className="gss-field">
              <label>Feuille (vide = la première)</label>
              <input className="gss-input" value={feuille}
                onChange={(e) => { invalider(); setFeuille(e.target.value); }} />
            </div>
            <div className="gss-field">
              <label>Ligne des intitulés</label>
              <input className="gss-input" type="number" min="1" value={ligneEntetes}
                onChange={(e) => { invalider(); setLigneEntetes(e.target.value); }} />
            </div>
          </div>

          {/* ── LES COLONNES, SAISIES ──────────────────────────────────── */}
          <div className="gss-field">
            <label>Intitulé de la colonne du matricule</label>
            <input className="gss-input" value={colMatricule}
              onChange={(e) => { invalider(); setColMatricule(e.target.value); }} />
          </div>

          {forme === 'colonnes' ? (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div className="gss-field">
                <label>Intitulé de la colonne de la date</label>
                <input className="gss-input" value={colDate}
                  onChange={(e) => { invalider(); setColDate(e.target.value); }} />
              </div>
              <div className="gss-field">
                <label>Intitulé de la colonne du statut</label>
                <input className="gss-input" value={colStatut}
                  onChange={(e) => { invalider(); setColStatut(e.target.value); }} />
              </div>
            </div>
          ) : (
            <div className="gss-field">
              <label>
                Intitulé des colonnes de jour — écrire <code>#</code> à la place du numéro
              </label>
              <input className="gss-input" value={motifJour}
                onChange={(e) => { invalider(); setMotifJour(e.target.value); }} />
              <div style={{ fontSize: 12, color: '#666', marginTop: 4 }}>
                {motifJour
                  ? `${joursDuMois} colonnes seront cherchées, du « ${motifJour.replace(/#/g, '1')} »`
                    + ` au « ${motifJour.replace(/#/g, String(joursDuMois))} ».`
                  : `${joursDuMois} colonnes seront cherchées, une par jour du mois.`}
              </div>
            </div>
          )}

          {/* ── LES ÉQUIVALENTS DE STATUT ──────────────────────────────── */}
          <div style={{ marginTop: 14, borderTop: '1px solid #eee', paddingTop: 12 }}>
            <div style={{ fontWeight: 700, marginBottom: 4 }}>
              Équivalents des statuts, tels qu ils sont écrits dans le classeur
            </div>
            <div style={{ fontSize: 12.5, color: '#666', marginBottom: 10 }}>
              Plusieurs valeurs se séparent par une virgule. Une valeur de cellule
              qui ne figure dans aucune ligne ci-dessous sera <strong>refusée et nommée</strong> :
              un statut dont le sens est inconnu ne se paie pas et ne se déduit pas.
            </div>
            {CIBLES.map((c) => (
              <div key={c.cle} style={{ display: 'grid', gridTemplateColumns: '300px 1fr', gap: 10, marginBottom: 6, alignItems: 'center' }}>
                <div style={{ fontSize: 13 }}>{c.libelle}</div>
                <input className="gss-input" value={equivalents[c.cle] || ''}
                  onChange={(e) => majEquivalent(c.cle, e.target.value)} />
              </div>
            ))}
          </div>

          {/* ── L'APERÇU ───────────────────────────────────────────────── */}
          {erreur && (
            <div style={{ border: '1px solid #FCA5A5', background: '#FEF2F2', borderRadius: 8, padding: 14, marginTop: 14, color: '#7F1D1D', fontSize: 13 }}>
              <strong>Refusé.</strong> {erreur}
            </div>
          )}

          {t && (
            <div style={{ border: '1px solid #ddd', borderRadius: 8, padding: 14, marginTop: 14 }}>
              <div style={{ fontWeight: 800, marginBottom: 8 }}>
                {apercu.ecrit ? 'Écrit' : 'Aperçu'} — feuille « {apercu.feuille} »,
                {' '}du {apercu.periode.debut} au {apercu.periode.fin}
              </div>
              <div style={{ display: 'flex', gap: 18, flexWrap: 'wrap', fontSize: 13, marginBottom: 10 }}>
                <div>Lignes lues : <strong>{t.lignes_lues}</strong></div>
                <div>{apercu.ecrit ? 'Écrites' : 'Retenues'} : <strong>{t.retenues}</strong></div>
                <div>Agents : <strong>{t.agents_concernes}</strong></div>
                <div style={{ color: t.refusees ? '#B91C1C' : '#2e7d32' }}>
                  Refusées : <strong>{t.refusees}</strong>
                </div>
                <div style={{ color: t.a_remplacer ? '#f57c00' : '#666' }}>
                  À remplacer : <strong>{t.a_remplacer}</strong>
                </div>
              </div>
              <div style={{ fontSize: 13, marginBottom: 10 }}>{apercu.message}</div>

              {/* ⚠️ LES REFUS, PAR MOTIF, AVEC LES LIGNES. Un compteur sans les
                  lignes n'aide personne à corriger son classeur. */}
              {(apercu.refus_par_motif || []).map((m) => (
                <div key={m.motif} style={{ marginBottom: 10 }}>
                  <div style={{ fontWeight: 700, fontSize: 13, color: '#B91C1C' }}>
                    {LIBELLE_MOTIF[m.motif] || m.motif} — {m.effectif} ligne(s)
                  </div>
                  <ul style={{ margin: '4px 0 0 18px', fontSize: 12, color: '#7F1D1D', lineHeight: 1.55 }}>
                    {m.lignes.slice(0, 10).map((l, i) => <li key={i}>{l.detail}</li>)}
                    {m.effectif > m.lignes.length && (
                      <li style={{ listStyle: 'none', color: '#999' }}>
                        … et {m.effectif - m.lignes.length} autre(s) de même motif.
                      </li>
                    )}
                  </ul>
                </div>
              ))}

              {/* ── LES DÉMISSIONS POINTÉES, ET CE QU'ELLES N'ONT PAS FAIT ─────
                  ⛔ L'IMPORT N'ENREGISTRE AUCUNE SORTIE. Une journée de démission
                     se pointe comme un fait constaté ; sortir l'agent de
                     l'effectif est un acte distinct, confirmé par une personne
                     habilitée, et tracé — le même point d'écriture que l'écran de
                     sortie. *Faire sortir 80 agents parce qu'un classeur portait
                     un code, c'est décider de leur contrat depuis un tableur.*
                  ⓘ Ce n'est pas un refus : la ligne est écrite. C'est un acte
                    qui RESTE à faire, et l'écran le nomme pour qu'il ne s'oublie
                    pas — une sortie non enregistrée laisse l'agent payé le mois
                    suivant. */}
              {(apercu.demissions || []).length > 0 && (
                <div style={{ marginTop: 8, padding: 10, borderRadius: 6, background: '#EFEBE9', border: '1px solid #5d4037' }}>
                  <div style={{ fontWeight: 700, fontSize: 13, color: '#5d4037' }}>
                    Démission(s) pointée(s) — la sortie reste à enregistrer
                  </div>
                  <ul style={{ margin: '4px 0 0 18px', fontSize: 12, lineHeight: 1.55 }}>
                    {apercu.demissions.slice(0, 10).map((x, i) => (
                      <li key={i}>
                        {x.message} — <strong>{x.action || 'Enregistrer la sortie'}</strong>
                        {' '}(fiche de l'agent, motif et date à confirmer)
                      </li>
                    ))}
                    {apercu.demissions.length > 10 && (
                      <li style={{ listStyle: 'none', color: '#999' }}>
                        … et {apercu.demissions.length - 10} autre(s).
                      </li>
                    )}
                  </ul>
                  <div style={{ fontSize: 11, color: '#5d4037', marginTop: 4 }}>
                    Le pointage du jour est écrit. L'agent reste dans l'effectif
                    jusqu'à l'enregistrement de sa sortie.
                  </div>
                </div>
              )}

              {/* ── LES ÉCARTS ENTRE LES CONGÉS APPROUVÉS ET LE POINTAGE ────────
                  ⛔ CE N'EST PAS UN REFUS D'IMPORT — arbitrage de Dom : la
                     demande approuvée fait foi, le pointage constate le terrain,
                     et les deux se croisent sans que l'un annule l'autre. Mais
                     tant que l'écart dure, le contrôle pré-paie refusera l'agent
                     et le nommera. *L'import dit la vérité tôt ; la paie refuse
                     tard.* */}
              {(apercu.alertes || []).length > 0 && (
                <div style={{ marginTop: 8, padding: 10, borderRadius: 6, background: '#E3F2FD', border: '1px solid #1565c0' }}>
                  <div style={{ fontWeight: 700, fontSize: 13, color: '#1565c0' }}>
                    Écart(s) entre les congés approuvés et le pointage — {apercu.alertes.length}
                  </div>
                  <ul style={{ margin: '4px 0 0 18px', fontSize: 12, lineHeight: 1.55 }}>
                    {apercu.alertes.slice(0, 10).map((x, i) => <li key={i}>{x.message}</li>)}
                    {apercu.alertes.length > 10 && (
                      <li style={{ listStyle: 'none', color: '#999' }}>
                        … et {apercu.alertes.length - 10} autre(s).
                      </li>
                    )}
                  </ul>
                  <div style={{ fontSize: 11, color: '#1565c0', marginTop: 4 }}>
                    L'import n'est pas refusé pour cela. Se résout par une
                    régularisation datée de la demande, ou par une correction du
                    pointage — au choix de la personne qui sait ce qui s'est passé.
                  </div>
                </div>
              )}

              {/* ⚠️ CE QUI SERA ÉCRASÉ SE MONTRE AVANT, avec la valeur d'avant
                  et celle d'après. */}
              {(apercu.a_remplacer || []).length > 0 && (
                <div style={{ marginTop: 8 }}>
                  <div style={{ fontWeight: 700, fontSize: 13, color: '#f57c00' }}>
                    Pointages qui seront remplacés
                  </div>
                  <ul style={{ margin: '4px 0 0 18px', fontSize: 12, lineHeight: 1.55 }}>
                    {apercu.a_remplacer.slice(0, 10).map((r, i) => (
                      <li key={i}>
                        {r.matricule} — {r.date} : « {r.avant} » devient « {r.apres} »
                      </li>
                    ))}
                    {apercu.a_remplacer.length > 10 && (
                      <li style={{ listStyle: 'none', color: '#999' }}>
                        … et {apercu.a_remplacer.length - 10} autre(s).
                      </li>
                    )}
                  </ul>
                </div>
              )}

              {/* ⓘ Le remplacement n'est PROPOSÉ que s'il y a quelque chose à
                  remplacer — voir la note en tête de fichier. */}
              {!apercu.ecrit && dejaPointes > 0 && !remplacer && (
                <label style={{ display: 'block', marginTop: 12, fontSize: 13 }}>
                  <input type="checkbox" checked={remplacer}
                    onChange={(e) => { setRemplacer(e.target.checked); setApercu(null); }} />
                  {' '}Remplacer les {dejaPointes} pointage(s) déjà saisi(s) — un nouvel aperçu
                  montrera ce qui change avant toute écriture.
                </label>
              )}
            </div>
          )}
        </div>

        <div className="gss-modal-actions">
          <button className="gss-btn-cancel" onClick={onFerme}>Fermer</button>
          <button className="gss-btn" disabled={!fichier || enCours === 'apercu'}
            onClick={demanderApercu}>
            {enCours === 'apercu' ? 'Lecture…' : 'Aperçu'}
          </button>
          {/* ⛔ L'ÉCRITURE EST FERMÉE TANT QUE L'APERÇU N'A PAS EU LIEU, et tant
              qu'il ne retient aucune ligne. */}
          <button className="gss-btn-deploy"
            disabled={!t || apercu.ecrit || t.retenues === 0 || enCours === 'ecriture'}
            onClick={ecrire}>
            {enCours === 'ecriture' ? 'Écriture…'
              : t ? `Confirmer l écriture de ${t.retenues} pointage(s)`
                : 'Confirmer l écriture'}
          </button>
        </div>
      </div>
    </div>
  );
}
